/**
 * Everything the about page says, in one place.
 *
 * The sections below are composition and motion only — no copy lives inside
 * them. Copy written inline would mean a file to open per section when a
 * number changes, and the figures here are the same ones the home page
 * quotes, so they have to be changeable together.
 *
 * Figures are the site's existing claims (home page KPI rail and the old
 * about page stats). Nothing here is invented beyond what the site already
 * published.
 */

/* ------------------------------------------------------------------ hero */

export const HERO = {
  label: "About Mondoway",
  lede:
    "Jordan's first and only all-in-one smart application, built to simplify daily life by bringing everything you need into a single, seamless platform.",
  /* The rail that closes the hero band. `display` opts a figure out of the
     count-up when it is not a number. */
  stats: [
    { icon: "checkCircle", value: 150, suffix: "+", label: "Projects Delivered" },
    { icon: "clock", value: 8, suffix: "+", label: "Years Experience" },
    { icon: "users", value: 75, suffix: "+", label: "Happy Clients" },
    { icon: "chat", display: "24/7", label: "Support" }
  ]
};

/* ----------------------------------------------------------- who we are */

export const WHO = {
  label: "Who we are",
  index: "01",
  /* Read as one sentence that brightens word by word on scroll, so it is
     stored as a plain string and split at render. */
  statement:
    "We are not another app on the pile. We are the one place the whole day happens — what you order, where you travel, what you buy, who you book.",
  aside: [
    {
      heading: "The problem",
      body:
        "Eight apps, eight logins, eight checkouts, eight ways for something to go wrong."
    },
    {
      heading: "The answer",
      body:
        "One account. One checkout. Verified businesses. Everything tracked in the same place."
    }
  ]
};

/* --------------------------------------------------------------- story */

export const STORY = {
  label: "Our story",
  index: "02",
  title: "Eight years, one idea.",
  chapters: [
    {
      year: "2017",
      title: "A team, a room, an idea",
      body:
        "Mondoway starts in Amman with a small group of developers and designers, and a question nobody had answered well: why does daily life need this many apps?"
    },
    {
      year: "2019",
      title: "The first fifty",
      body:
        "Fifty projects delivered. The idea stops being a pitch and starts being a track record, built one client at a time."
    },
    {
      year: "2021",
      title: "Past the border",
      body:
        "The work moves out across the Middle East. What was built for Amman turns out to travel — the problem was never local."
    },
    {
      year: "2023",
      title: "Smarter by default",
      body:
        "AI moves from a feature to plumbing: search that understands intent, recommendations worth reading, support that answers at three in the morning."
    },
    {
      year: "2025",
      title: "Nine categories, one login",
      body:
        "Shopping, travel, food, property, healthcare and more, on one account — the platform the first room was arguing about."
    }
  ]
};

/* ----------------------------------------------------- mission & vision */

export const PURPOSE = {
  label: "Mission & vision",
  index: "03",
  mission: {
    kicker: "Mission",
    title: "Make the day simpler.",
    body:
      "Give people one trustworthy place for the things they do every day, and give the businesses they buy from a platform that treats them as partners rather than listings.",
    points: ["One account, every service", "Verified before it lists", "Built around real habits"]
  },
  vision: {
    kicker: "Vision",
    title: "The default, everywhere.",
    body:
      "Start where we are understood best and grow outward — until reaching for one app to sort out a day is simply what people do, in every market we enter.",
    points: ["Jordan first, region next", "Infrastructure that scales", "Local knowledge, global reach"]
  }
};

/* -------------------------------------------------------------- values */

export const VALUES = {
  label: "Values",
  index: "04",
  title: "What we believe.",
  items: [
    {
      n: "01",
      title: "Innovation",
      body:
        "New technology earns its place by making something simpler, or it does not ship at all.",
      tag: "Ship the useful part"
    },
    {
      n: "02",
      title: "Trust",
      body:
        "Every business is verified before it lists. Accountability is the product, not a policy page.",
      tag: "Verified, not assumed"
    },
    {
      n: "03",
      title: "Excellence",
      body:
        "Finished properly beats finished quickly. The detail nobody notices is the one holding it up.",
      tag: "Done, then good"
    },
    {
      n: "04",
      title: "Growth",
      body:
        "Ours follows our partners'. A platform that grows while its sellers stall has solved nothing.",
      tag: "Theirs before ours"
    },
    {
      n: "05",
      title: "Impact",
      body:
        "Measured in hours people get back and businesses that reach further than they could alone.",
      tag: "Hours, not clicks"
    }
  ]
};

/* -------------------------------------------------------------- impact */

export const IMPACT = {
  label: "Impact",
  index: "05",
  title: "The numbers behind it.",
  /* `display` opts a figure out of the count-up when it is not a number. */
  primary: [
    { value: 150, suffix: "+", label: "Projects delivered", note: "Since 2017" },
    { value: 75, suffix: "+", label: "Clients served", note: "Jordan & the region" }
  ],
  secondary: [
    { value: 2, suffix: "M", label: "Hotels worldwide" },
    { value: 460, label: "Airline partners" },
    { value: 250, label: "Carriers" },
    { value: 200, label: "eSIM services" },
    { value: 9, label: "Categories" },
    { display: "24/7", label: "Support" }
  ]
};

/* -------------------------------------------------------- philosophy */

export const PHILOSOPHY = {
  label: "Culture",
  index: "06",
  /* Set at display size across three lines, so the break points are a design
     decision rather than whatever the text happens to wrap to. */
  lines: [["We", "don't", "build"], ["more", "apps."], ["We", "build", "the", "one."]],
  body:
    "A small team that argues about the details, ships on Fridays, and treats every verified business on the platform as somebody's livelihood — because it is."
};

/* --------------------------------------------------------------- cta */

export const CTA = {
  label: "What's next",
  index: "07",
  lines: [["Let's", "build"], ["what's", "next."]],
  body: "Tell us what you are trying to make simpler. We will tell you if we can help.",
  action: { label: "Start a conversation", to: "/contact" },
  secondary: { label: "Explore services", to: "/services" }
};
